import type React from "react";
import type { Camera, Object3D, WebGLRenderer } from "~/lib/three";
import {
  DoubleSide,
  NearestFilter,
  NoBlending,
  Points,
  Scene,
  ShaderMaterial,
  WebGLRenderTarget,
} from "~/lib/three";
import type { ThreeDObject } from "./use-three-d-records";

/*
 * The picking material will position points in the same manner as the standard
 * point material while giving each point a unique "color" derived from the
 * index of its source topic and its index in its parent cloud. Since WebGL only
 * guarantees 8-bit RGBA channels for reading pixels, we're working with 32 bits
 * here.
 *
 * Since we support a max of 6 point clouds per panel, we can use 3 bits to
 * encode the cloud index (same as the source topic index). The remaining 29
 * bits can encode a point's index within its parent cloud. The RGBA color
 * (0, 0, 0, 0) is reserved to indicate empty space where there isn't any point.
 *
 * For the actual encoding:
 *  - The 3-bit cloud index will occupy the 3 most significant bits of the red
 *    channel.
 *  - The point index, treated as a 29-bit number, will have its 5 most
 *    significant bits occupy the 5 least significant bits of the red channel,
 *    its next 8 most significant bits occupy the entire green channel, its next
 *    8 most significant bits occupy the entire blue channel, and its 8 least
 *    significant bits occupy the entire alpha channel.
 */
const vertexShader = `
uniform float u_point_size;
uniform highp int u_cloud_id;

varying vec4 v_color;

void main() {
  // Start indexing from 1 so no rendered point ever has a color of 
  // (0, 0, 0, 0), the empty space signifier.
  int id = gl_VertexID + 1;
  
  int r = ((u_cloud_id & 7) << 5) | ((id >> 24) & 31);
  int g = (id >> 16) & 255;
  int b = (id >> 8) & 255;
  int a = id & 255;

  v_color = vec4(
    float(r) / 255.0,
    float(g) / 255.0,
    float(b) / 255.0,
    float(a) / 255.0
  );
  
  // 'projectionMatrix', 'modelViewMatrix', and 'position' are included by
  // Three.js
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = u_point_size;
}`;

const fragmentShader = `
varying vec4 v_color;

void main() {
  gl_FragColor = v_color;
}`;

export function performGpuPick(
  gl: WebGLRenderer,
  camera: Camera,
  root: Object3D,
  objects: ReadonlyArray<ThreeDObject>,
  e: React.MouseEvent<HTMLElement>,
) {
  // For simplicity, the render target needs have the same dimensions as the
  // <canvas>. Most tutorials for GPU picking suggest making a 1x1 render
  // target along with a 1x1 camera view offset to render the minimum necessary
  // to pick a color. Unfortunately, that doesn't seem to work for us, likely
  // because we're rendering `GL_POINTS` primitives which are entirely clipped
  // if the point's center is outside the viewport. Instead, just render the
  // entire scene so it's certain its points' positions match the onscreen
  // scene's points exactly.
  const pickingTarget = new WebGLRenderTarget(
    gl.getContext().drawingBufferWidth,
    gl.getContext().drawingBufferHeight,
    { minFilter: NearestFilter, magFilter: NearestFilter },
  );
  const createdMaterials = new Array<ShaderMaterial>();

  try {
    return performGpuPickImpl(
      gl,
      camera,
      root,
      objects,
      e,
      pickingTarget,
      createdMaterials,
    );
  } finally {
    pickingTarget.dispose();
    createdMaterials.forEach((material) => {
      material.dispose();
    });
  }
}

function performGpuPickImpl(
  gl: WebGLRenderer,
  camera: Camera,
  root: Object3D,
  objects: ReadonlyArray<ThreeDObject>,
  e: React.MouseEvent<HTMLElement>,
  pickingTarget: WebGLRenderTarget,
  createdMaterials: Array<ShaderMaterial>,
): {
  object: ThreeDObject;
  mesh: Points;
  pointIndex: number;
} | null {
  const pickingScene = new Scene();

  const meshMap = new Map<number, Points>();

  root.traverse((child) => {
    if (!(child instanceof Points)) {
      return;
    }

    // Ideally we'd just be able to clone the entire scene including the
    // transform controls. Trying to do that throws an error because transform
    // controls can't be cloned. Rendering the descendant points without their
    // ancestor transform controls causes them to be misaligned if the user
    // rotated the controls any. To work around this, just copy the points'
    // world matrix (which currently accounts for any ancestor transform
    // controls' transforms) into the points' local matrix.
    const clonedChild = child.clone(true);
    clonedChild.matrix.copy(clonedChild.matrixWorld);
    clonedChild.matrixAutoUpdate = false;

    const cloudIndex = objects.findIndex(
      (object) => object.data.data === clonedChild.geometry,
    );

    const material = createPickingMaterial(
      objects[cloudIndex],
      gl.getPixelRatio(),
      cloudIndex,
    );

    createdMaterials.push(material);
    clonedChild.material = material;

    pickingScene.add(clonedChild);
    meshMap.set(cloudIndex, clonedChild);
  });

  gl.setRenderTarget(pickingTarget);
  gl.render(pickingScene, camera);
  gl.setRenderTarget(null);

  const buffer = new Uint8Array(4);
  gl.readRenderTargetPixels(
    pickingTarget,
    // The render/picking target uses device pixels while the pointer event is
    // using CSS pixels.
    e.nativeEvent.offsetX * gl.getPixelRatio(),
    pickingTarget.height - e.nativeEvent.offsetY * gl.getPixelRatio(),
    1,
    1,
    buffer,
  );

  // All 0s is reserved for empty space where there are no points.
  if (buffer.every((byte) => byte === 0)) {
    return null;
  }

  const [r, g, b, a] = buffer;

  const cloudIndex = (r >> 5) & 0b111;

  // Point indices in the shader start at 1 so no point ever has a color of
  // (0, 0, 0, 0), the empty space signifier. Convert back to a 0-based index
  // here.
  // prettier-ignore
  const pointIndex = (
    ((r & 0b11111) << 24) |
    (g << 16) |
    (b << 8) |
    a
  ) - 1;

  return {
    object: objects[cloudIndex],
    mesh: meshMap.get(cloudIndex)!,
    pointIndex,
  };
}

function createPickingMaterial(
  object: ThreeDObject,
  devicePixelRatio: number,
  cloudIndex: number,
) {
  const pointSize = Math.max(object.size, 16);

  return new ShaderMaterial({
    vertexShader,
    fragmentShader,
    side: DoubleSide,
    blending: NoBlending,
    uniforms: {
      u_point_size: { value: pointSize * devicePixelRatio },
      u_cloud_id: { value: cloudIndex },
    },
  });
}
